/**
 * Unified Create Flow store — tracks state across design, copywriter, and composer tools.
 *
 * Enables cross-tool navigation: Design Studio -> Copywriter -> Post Composer -> Preview -> Schedule
 *
 * Wired across the app: the design editor + studio home ("Use in post"), the
 * copywriter, brand voice, hashtag research, calendar and post detail write to
 * it; PostComposer reads it (any tool → compose handoff).
 */

import { createAppStore } from "@plerity/dashboard-shared/stores";
import type { AttachedMedia } from "../types/social";

type CreateFlowStep = "design" | "copywrite" | "compose" | "preview" | "schedule";

interface CreateFlowState {
  // IDs linking to existing records
  designId: string | null;
  /**
   * The existing post the composer is EDITING (`?post=<id>` edit mode). Set
   * when edit mode seeds the store; survives a Design Studio round trip so
   * returning to the compose tab keeps editing that post. null = a new post.
   */
  postId: string | null;
  /** AI copywriter generation the caption came from (→ metadata.copywriter_generation_id). */
  generationId: string | null;
  /** Which variant of that generation (per-platform index) was used. */
  generationVariantIndex: number | null;
  /** Content idea the post was started from (Content Ideas "Write it now") → metadata.content_idea_id. */
  contentIdeaId: string | null;

  // Content state carried across tools
  caption: string;
  hashtags: string[];
  /** Free-form campaign tags (migration 083, lane F5 2026-10-06) — internal
   * reporting only (GET /api/analytics/tags), never published to a platform. */
  tags: string[];
  // Attached media carries its kind (image | video) so the composer strip and
  // platform preview render a `<video>` frame for videos instead of a broken
  // `<img>`. A design thumbnail attached via `setDesign` is always an image.
  mediaUrls: AttachedMedia[];
  platforms: Array<{ platform: string; account_id: string }>;
  scheduledAt: string | null;

  // Navigation
  currentStep: CreateFlowStep;
  previousSteps: CreateFlowStep[];

  // Actions
  setDesign: (designId: string, thumbnailUrl?: string) => void;
  setPost: (postId: string | null) => void;
  setContentIdea: (contentIdeaId: string | null) => void;
  setCopywriterResult: (params: {
    generationId: string;
    caption: string;
    hashtags?: string[];
    variantIndex?: number | null;
  }) => void;
  setCaption: (caption: string) => void;
  setHashtags: (hashtags: string[]) => void;
  setTags: (tags: string[]) => void;
  setMediaUrls: (media: AttachedMedia[]) => void;
  addMediaUrl: (media: AttachedMedia) => void;
  setPlatforms: (platforms: Array<{ platform: string; account_id: string }>) => void;
  setScheduledAt: (scheduledAt: string | null) => void;
  navigateTo: (step: CreateFlowStep) => void;
  goBack: () => void;
  reset: () => void;
}

const initialState = {
  designId: null as string | null,
  postId: null as string | null,
  generationId: null as string | null,
  generationVariantIndex: null as number | null,
  contentIdeaId: null as string | null,
  caption: "",
  hashtags: [] as string[],
  tags: [] as string[],
  mediaUrls: [] as AttachedMedia[],
  platforms: [] as Array<{ platform: string; account_id: string }>,
  scheduledAt: null as string | null,
  currentStep: "compose" as CreateFlowStep,
  previousSteps: [] as CreateFlowStep[],
};

export const useCreateFlowStore = createAppStore<CreateFlowState>("social-create-flow", (set) => ({
  ...initialState,

  setDesign: (designId, thumbnailUrl) =>
    set((s) => ({
      designId,
      // A design thumbnail is always an image; move an existing entry with the
      // same URL to the front without duplicating it.
      mediaUrls: thumbnailUrl
        ? [{ url: thumbnailUrl, type: "image" as const }, ...s.mediaUrls.filter((m) => m.url !== thumbnailUrl)]
        : s.mediaUrls,
    })),

  setPost: (postId) => set({ postId }),
  setContentIdea: (contentIdeaId) => set({ contentIdeaId }),

  setCopywriterResult: ({ generationId, caption, hashtags, variantIndex }) =>
    set((s) => ({
      generationId,
      generationVariantIndex: variantIndex ?? null,
      caption,
      hashtags: hashtags ?? s.hashtags,
    })),

  setCaption: (caption) => set({ caption }),
  setHashtags: (hashtags) => set({ hashtags }),
  setTags: (tags) => set({ tags }),
  setMediaUrls: (mediaUrls) => set({ mediaUrls }),
  addMediaUrl: (media) => set((s) => ({ mediaUrls: [...s.mediaUrls, media] })),
  setPlatforms: (platforms) => set({ platforms }),
  setScheduledAt: (scheduledAt) => set({ scheduledAt }),

  navigateTo: (step) =>
    set((s) => ({
      currentStep: step,
      previousSteps: [...s.previousSteps, s.currentStep],
    })),

  goBack: () =>
    set((s) => {
      const prev = [...s.previousSteps];
      const last = prev.pop();
      return {
        currentStep: last ?? "compose",
        previousSteps: prev,
      };
    }),

  reset: () => set(initialState),
}));
